import { useState, useEffect } from 'react';
import { buildApiUrl } from '../config/api';

export interface SiteSettings {
  contactEmail: string;
  contactPhone: string;
  address: string;
  siteName: string;
  siteDescription: string;
  facebookUrl: string;
  instagramUrl: string;
  linkedinUrl: string;
  twitterUrl: string;
  // Hero de la page d'accueil (titre sur deux lignes + sous-titre).
  homeHeroTitleLine1: string;
  homeHeroTitleLine2: string;
  homeHeroSubtitle: string;
}

/**
 * Valeurs de repli, utilisées tant que l'API n'a pas répondu — ou si elle est
 * indisponible. Elles reprennent ce que le site affichait quand ces textes
 * étaient codés en dur : une panne de l'API ne doit pas faire disparaître
 * l'adresse de contact des mentions légales.
 *
 * Les champs vides le sont volontairement : aucun numéro de téléphone ni
 * compte social n'existe à ce jour. Les composants ne rendent que ce qui est
 * renseigné.
 */
const FALLBACK: SiteSettings = {
  contactEmail: 'contact@phoelynx.fr',
  contactPhone: '',
  address: '',
  siteName: 'Phoelynx France',
  siteDescription: '',
  facebookUrl: '',
  instagramUrl: '',
  linkedinUrl: '',
  twitterUrl: '',
  // Textes actuels du hero, conservés si l'admin ne les personnalise pas.
  homeHeroTitleLine1: 'CRÉATEUR DE',
  homeHeroTitleLine2: 'FUTURS ROBOTICIENS',
  homeHeroSubtitle: 'Formation technique en informatique, électronique et robotique.',
};

/** L'API renvoie { clé: { value, type, description } }. */
const extract = (payload: Record<string, { value: unknown }>): SiteSettings => {
  const read = (key: keyof SiteSettings): string => {
    const raw = payload[key]?.value;
    return typeof raw === 'string' && raw.trim() ? raw : FALLBACK[key];
  };

  return {
    contactEmail: read('contactEmail'),
    contactPhone: read('contactPhone'),
    address: read('address'),
    siteName: read('siteName'),
    siteDescription: read('siteDescription'),
    facebookUrl: read('facebookUrl'),
    instagramUrl: read('instagramUrl'),
    linkedinUrl: read('linkedinUrl'),
    twitterUrl: read('twitterUrl'),
    homeHeroTitleLine1: read('homeHeroTitleLine1'),
    homeHeroTitleLine2: read('homeHeroTitleLine2'),
    homeHeroSubtitle: read('homeHeroSubtitle'),
  };
};

export const useSiteSettings = (): SiteSettings => {
  const [settings, setSettings] = useState<SiteSettings>(FALLBACK);

  useEffect(() => {
    let cancelled = false;

    fetch(buildApiUrl('/settings/public'))
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((data) => {
        if (!cancelled) setSettings(extract(data));
      })
      .catch(() => {
        // On conserve les valeurs de repli : mieux vaut une adresse de contact
        // légèrement datée qu'une page de mentions légales sans contact.
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return settings;
};
